import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { 
  Building, Plus, Search, Edit, Trash2, Users, 
  Settings, Calendar, MoreVertical, Check, X 
} from 'lucide-react';

const BusinessUnits = () => {
  const { currentUser } = useAuth();
  const [businessUnits, setBusinessUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewBUForm, setShowNewBUForm] = useState(false);
  const [editingBU, setEditingBU] = useState(null);

  const [newBU, setNewBU] = useState({
    name: '',
    identifier: '',
    description: '',
    contactEmail: '',
    status: 'active'
  });

  useEffect(() => {
    fetchBusinessUnits();
  }, []);

  const fetchBusinessUnits = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/business-units', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBusinessUnits(data.businessUnits || []);
      }
    } catch (error) {
      console.error('Error fetching business units:', error);
      setBusinessUnits([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBU = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/business-units', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newBU)
      });

      if (response.ok) {
        fetchBusinessUnits();
        setNewBU({
          name: '',
          identifier: '',
          description: '',
          contactEmail: '',
          status: 'active'
        });
        setShowNewBUForm(false);
      }
    } catch (error) {
      console.error('Error creating business unit:', error);
    }
  };

  const filteredBusinessUnits = businessUnits.filter(bu => 
    bu.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    bu.identifier?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    bu.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-slate-700 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3,4,5,6].map(i => (
              <div key={i} className="h-48 bg-slate-700 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-100">Business Units</h1>
          <p className="text-slate-400 mt-1">Manage business unit configurations and client portal access</p>
        </div>
        <button
          onClick={() => setShowNewBUForm(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          New Business Unit
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="w-5 h-5 absolute left-3 top-3 text-slate-400" />
          <input
            type="text"
            placeholder="Search business units..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200"
          />
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-slate-800 p-4 rounded-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total Units</p>
              <p className="text-xl font-bold text-slate-100">{businessUnits.length}</p>
            </div>
            <Building className="w-8 h-8 text-blue-400" />
          </div>
        </div>

        <div className="bg-slate-800 p-4 rounded-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Active Units</p>
              <p className="text-xl font-bold text-slate-100">
                {businessUnits.filter(bu => bu.status === 'active').length}
              </p>
            </div>
            <Check className="w-8 h-8 text-green-400" />
          </div>
        </div>

        <div className="bg-slate-800 p-4 rounded-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Total Users</p>
              <p className="text-xl font-bold text-slate-100">
                {businessUnits.reduce((sum, bu) => sum + (bu.userCount || 0), 0)}
              </p>
            </div>
            <Users className="w-8 h-8 text-purple-400" />
          </div>
        </div>

        <div className="bg-slate-800 p-4 rounded-lg border border-slate-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-slate-400 text-sm">Portal Access</p>
              <p className="text-xl font-bold text-slate-100">
                {businessUnits.filter(bu => bu.portalEnabled).length}
              </p>
            </div>
            <Settings className="w-8 h-8 text-yellow-400" />
          </div>
        </div>
      </div>

      {/* Business Units Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredBusinessUnits.length === 0 ? (
          <div className="col-span-full bg-slate-800 rounded-lg border border-slate-700 p-12 text-center">
            <Building className="w-16 h-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-300 mb-2">No business units found</h3>
            <p className="text-slate-400 mb-4">
              {searchTerm ? 'No business units match your search' : 'Create the first business unit to get started'}
            </p>
            {!searchTerm && (
              <button
                onClick={() => setShowNewBUForm(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Create Business Unit
              </button>
            )}
          </div>
        ) : (
          filteredBusinessUnits.map(bu => (
            <div key={bu.id} className="bg-slate-800 rounded-lg border border-slate-700 p-6">
              {/* Card Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-600/20 rounded-lg">
                    <Building className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-100">{bu.name}</h3>
                    <p className="text-slate-400 text-sm">ID: {bu.identifier}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button className="p-1 text-slate-400 hover:text-slate-300 hover:bg-slate-700 rounded">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button className="p-1 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded">
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <button className="p-1 text-slate-400 hover:text-slate-300 hover:bg-slate-700 rounded">
                    <MoreVertical className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Description */}
              <p className="text-slate-300 text-sm mb-4 line-clamp-2">
                {bu.description || 'No description provided'}
              </p>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-slate-700/30 p-3 rounded">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-400 text-xs">Users</span>
                  </div>
                  <p className="text-slate-200 font-semibold">{bu.userCount || 0}</p>
                </div>
                <div className="bg-slate-700/30 p-3 rounded">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-400 text-xs">Created</span>
                  </div>
                  <p className="text-slate-200 text-sm">
                    {bu.createdAt ? new Date(bu.createdAt).toLocaleDateString() : 'Unknown'}
                  </p>
                </div>
              </div>

              {/* Status and Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-slate-700">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    bu.status === 'active' 
                      ? 'bg-green-900/30 text-green-400' 
                      : 'bg-red-900/30 text-red-400'
                  }`}>
                    {bu.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                  {bu.portalEnabled && (
                    <span className="px-2 py-1 text-xs rounded-full bg-blue-900/30 text-blue-400">
                      Portal Enabled
                    </span>
                  )}
                </div>
                
                <button className="text-slate-400 hover:text-blue-400 text-sm">
                  Configure →
                </button>
              </div>

              {/* Contact */}
              {bu.contactEmail && (
                <div className="mt-3 pt-3 border-t border-slate-700">
                  <p className="text-slate-400 text-xs">Contact: {bu.contactEmail}</p>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* New Business Unit Modal */}
      {showNewBUForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg border border-slate-700 p-6 w-full max-w-lg mx-4">
            <h2 className="text-2xl font-bold text-slate-100 mb-4">Create New Business Unit</h2>
            
            <form onSubmit={handleCreateBU}>
              <div className="mb-4">
                <label className="block text-slate-300 text-sm font-medium mb-2">
                  Business Unit Name *
                </label>
                <input
                  type="text"
                  value={newBU.name}
                  onChange={(e) => setNewBU({...newBU, name: e.target.value})}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-200"
                  placeholder="e.g., Marketing Department"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-slate-300 text-sm font-medium mb-2">
                  Identifier *
                </label>
                <input
                  type="text"
                  value={newBU.identifier}
                  onChange={(e) => setNewBU({...newBU, identifier: e.target.value})}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-200"
                  placeholder="e.g., marketing"
                  required
                />
                <p className="text-slate-400 text-xs mt-1">Used in URLs and system references</p>
              </div>

              <div className="mb-4">
                <label className="block text-slate-300 text-sm font-medium mb-2">
                  Description
                </label>
                <textarea
                  value={newBU.description}
                  onChange={(e) => setNewBU({...newBU, description: e.target.value})}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-200 h-20"
                  placeholder="Brief description of this business unit..."
                />
              </div>

              <div className="mb-6">
                <label className="block text-slate-300 text-sm font-medium mb-2">
                  Contact Email
                </label>
                <input
                  type="email"
                  value={newBU.contactEmail}
                  onChange={(e) => setNewBU({...newBU, contactEmail: e.target.value})}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-slate-200"
                  placeholder="contact@company.com"
                />
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowNewBUForm(false)}
                  className="px-4 py-2 text-slate-400 hover:text-slate-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg"
                >
                  Create Business Unit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessUnits;